import { Box, Group, Input, InputWrapperProps } from '@mantine/core';
import { FieldValues } from 'react-hook-form';
import { useEffect, useState } from 'react';

import MercuryButton from '../../button/Button';
import List from '../../list/List';

import { formatBytes } from '@/utils';

import type { BaseElement } from '@/types/form';

import classes from '../Form.module.css';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

interface MercuryFileProps<TFormValues extends FieldValues>
  extends Omit<InputWrapperProps, 'name'>,
    BaseElement<TFormValues> {
  extension?: string;
}

const MercuryFile = <TFormValues extends FieldValues>({
  register,
  name,
  formOptions,
  extension,
  watch,
  displayError,
  resetField,
  error,
  ...inputProps
}: MercuryFileProps<TFormValues>) => {
  const value = watch && watch(name);
  const isSet = value && value.length > 0;
  const hasTooBigFile =
    isSet && Array.from(value as any[]).some((file) => file.size > MAX_FILE_SIZE_BYTES);
  const [oversizeError, setOversizeError] = useState(false);

  useEffect(() => {
    if (hasTooBigFile) {
      setOversizeError(true);
      resetField?.(name);
    }
  }, [hasTooBigFile, name, resetField]);

  useEffect(() => {
    if (isSet && !hasTooBigFile) {
      setOversizeError(false);
    }
  }, [isSet, hasTooBigFile]);

  const validateMaxFileSize = (files: FileList | any): true | string => {
    if (!files || (files as any).length === 0) return true;
    const tooBig = Array.from(files as FileList).some((file) => file.size > MAX_FILE_SIZE_BYTES);
    return tooBig ? 'File size exceeds 20MB' : true;
  };

  const mergedFormOptions: any = {
    ...formOptions,
    validate: (() => {
      const existing: any = formOptions?.validate;
      if (!existing) return validateMaxFileSize;
      if (typeof existing === 'function') {
        return (val: any) => {
          const sizeResult = validateMaxFileSize(val);
          if (sizeResult !== true) return sizeResult;
          return existing(val);
        };
      }
      if (typeof existing === 'object') {
        return {
          ...existing,
          maxFileSize: validateMaxFileSize,
        };
      }
      return validateMaxFileSize;
    })(),
  };

  const onClear = () => {
    setOversizeError(false);
    resetField?.(name);
  };

  return (
    <Box>
      <Input.Label component="span">{inputProps.label}</Input.Label>

      <Input.Error>{error}</Input.Error>

      <Input
        {...register?.(name, mergedFormOptions)}
        id={name}
        type="file"
        display="none"
        multiple
      />

      <Group gap={15} mt={3} mb={inputProps.description ? 5 : 0}>
        <MercuryButton component="label" htmlFor={name}>
          Choose File {extension ? `(${extension})` : ''}
        </MercuryButton>

        {isSet && (
          <button className={classes.clearFiles} type="button" onClick={onClear}>
            Clear
          </button>
        )}
      </Group>

      <Input.Description component="span" mt={6}>
        Max file size: 20MB
      </Input.Description>

      {oversizeError && <Input.Error>File size exceeds 20MB</Input.Error>}

      {isSet && !oversizeError && (
        <List
          items={[...(value as any[])]}
          mt={15}
          gap={5}
          renderItem={(file) => (
            <li className={classes.file}>
              - {file.name} - <b>{formatBytes(file.size)}</b>
            </li>
          )}
        />
      )}

      {inputProps.description && (
        <Input.Description mt={5}>{inputProps.description}</Input.Description>
      )}
    </Box>
  );
};

export default MercuryFile;
